import React, { useState, useRef, useEffect } from 'react';
import {
    Input, TextField, Button, InputLabel, MenuItem, FormControl, Select, Box, FormHelperText, Autocomplete, InputAdornment, IconButton,
} from '@mui/material';
import { Formik, useFormik, Form, Field } from 'formik';
import * as yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Country, State, City } from 'country-state-city';

const nonEngChar = /[^-A-Za-z0-9_@,.:;{}/&%()'"#*<>!$?+\n ]/;
export const LOCATION_VALIDATIONS = yup.object().shape({
    country: yup.string().required('country cannot be empty.'),
    state: yup.string().required('state cannot be empty.'),
    city: yup.string().required('city cannot be empty.'),
    zip: yup.string()
        .matches(/^\d{5,}$/, 'Pincode must be at least 5 digits.')
        .test('non-eng-char', 'Pincode should not contain non-English characters.', value => !nonEngChar.test(value))
        .required('Pincode cannot be empty.'),
    companyAddress: yup.string()
        .max(500, 'Address cannot exceed 500 characters.')
        .test('non-eng-char', 'Address should not contain non-English characters.', value => !nonEngChar.test(value))
        .required('Address cannot be empty.'),
})

const Location = ({ initialValues,onSubmit, onBack, currentStep }) => {
    const initialState = {
        country: '',
        state: '',
        city: '',
        zip: '',
        companyAddress: '',
    }
    const navigate = useNavigate();
    const { loading } = useSelector((state) => state.auth);
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [states, setStates] = useState([]);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [cities, setCities] = useState([]);
    const [availableCountries, setAvailableCountries] = useState(countries);

    useEffect(() => {
        const fetchCountries = async () => {
            const countriesData = await Country.getAllCountries();
            const countriesWithDisabled = countriesData.map(country => ({
                value: country.isoCode,
                label: country.name,
                disabled: false
            }));
            setAvailableCountries(countriesWithDisabled);
            setCountries(countriesWithDisabled);
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        const fetchStates = async () => {
            const statesData = await State.getAllStates();
            setStates(statesData.map(state => ({ value: state.name, label: state.name })));
        };
        fetchStates();
    }, []);


    useEffect(() => {
        const fetchCities = async () => {
            const citiesData = await City.getAllCities();
            setCities(citiesData.map(city => ({ value: city.name, label: city.name })));
        };
        fetchCities();

    }, []);

    return (
        <>
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                // validationSchema={LOCATION_VALIDATIONS}
                onSubmit={(values, { setSubmitting }) => {
                    // const formattedValues = {
                    //     country: selectedCountry?.label || '',
                    //     state: selectedState?.label || '',
                    //     city: selectedCity?.label || '',
                    //     zip: values.zip || '',
                    //     companyAddress: values.companyAddress || '',
                    // };
                    // console.log('Formatted Submit:', formattedValues);
                    // onSubmit(formattedValues);
                    onSubmit(values);
                    setSubmitting(false);
                }}
            >
                {formikProps => {
                    const {
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        setFieldValue,
                    } = formikProps;
                    const handleCountryChange = (_, newValue) => {
                        console.log('New value inside handle country change ', newValue)
                        setSelectedCountry(newValue);
                        setFieldValue('country', newValue ? newValue.label : '');
                        if (newValue) {
                            const countryStates = State.getStatesOfCountry(newValue.value);
                            setStates(countryStates.map(state => ({ label: state.name, value: state.isoCode })));
                        } else {
                            setStates([]);
                            setCities([]);
                        }
                        setSelectedState(null); // Reset selected state when country changes
                        setSelectedCity(null); // Reset selected city when country changes
                    };

                    const handleStateChange = (_, newValue) => {
                        console.log('New value inside handle state change ', newValue)
                        setSelectedState(newValue);
                        setFieldValue('state', newValue ? newValue.label : '');
                        if (newValue) {
                            const stateCities = City.getCitiesOfState(selectedCountry?.value, newValue.value);
                            setCities(stateCities.map(city => ({ label: city.name, value: city.name })));
                        } else {
                            setCities([]);
                        }
                        setSelectedCity(null); // Reset selected city when state changes
                    };

                    const handleCityChange = (_, newValue) => {
                        console.log('New value inside handle city change ', newValue)
                        setSelectedCity(newValue);
                        setFieldValue('city', newValue ? newValue.label : '');
                    };                  
                    return (
                        <form form onSubmit={handleSubmit}>
                            <Box sx={{ display: 'flex', gap: 2 }}> </Box>
                            <div className="row">
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <div className="col-md-3 cs-form mb-3">
                                        <FormControl className='md-input' error={Boolean(touched.country && errors.country)}>
                                            <InputLabel id="country-label"></InputLabel>
                                            <Autocomplete
                                                id="country-select"
                                                options={countries}
                                                getOptionLabel={(option) => option.label}
                                                value={countries.find(country => country.label === values.country) || null}
                                                onChange={handleCountryChange}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Country" variant="outlined" error={touched.country && Boolean(errors.country)} helperText={touched.country && errors.country} />
                                                )}
                                            />
                                        </FormControl>
                                    </div>
                                    <div className="col-md-3 cs-form mb-3">
                                        <FormControl className='md-input' error={Boolean(touched.state && errors.state)}>
                                            <InputLabel id="state-label"></InputLabel>
                                            <Autocomplete
                                                id="state-select"
                                                options={states}
                                                getOptionLabel={(option) => option.label}
                                                value={states.find(state => state.label === values.state) || null}
                                                onChange={handleStateChange}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="State" variant="outlined" error={touched.state && Boolean(errors.state)} helperText={touched.state && errors.state} />
                                                )}
                                            />
                                        </FormControl>
                                    </div>
                                    <div className="col-md-3 cs-form mb-2">
                                        <FormControl className='md-input' error={Boolean(touched.state && errors.state)}>
                                            <InputLabel id="city-label"></InputLabel>
                                            <Autocomplete
                                                id="city-select"
                                                options={cities}
                                                getOptionLabel={(option) => option.label}
                                                value={cities.find(city => city.label === values.city) || null}
                                                onChange={handleCityChange}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="City" variant="outlined" error={touched.city && Boolean(errors.city)} helperText={touched.city && errors.city} />
                                                )}
                                            />
                                        </FormControl>
                                    </div>
                                    <div className="col-md-2.5 cs-form mb-3">
                                        <TextField
                                            label='Pincode *'
                                            name="zip"
                                            value={values.zip}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!errors.zip}
                                            helperText={errors.zip}
                                        />
                                    </div>
                                </Box>
                            </div>
                            <div className='row'>
                                <div className="col-md-12 cs-form mb-3">
                                    <TextField
                                        name="companyAddress"
                                        className='md-input'
                                        label='Address *'
                                        multiline
                                        rows={4}
                                        value={values.companyAddress}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.companyAddress && Boolean(errors.companyAddress)}
                                        helperText={touched.companyAddress && errors.companyAddress}
                                    />
                                </div>
                            </div>
                            <div className="d-flex justify-content-between mt-3">
                                <button type="button" className="btn btn-light grey-btn lg-btn"
                                    // onClick={() => handleStepChange(currentStep - 1)}>
                                    onClick={onBack}
                                    >
                                        
                                    Back
                                </button>
                                <div>
                                    <button type="submit" className="btn btn-primary greeen-btn lg-btn lg-btn"
                                        variant="contained"
                                        onClick={handleSubmit}
                                        disabled={loading}>
                                        {loading ? 'Submitting...' : 'Next'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )
                }
                }
            </Formik >
        </>
    )
}

export default Location